// packages/web/src/components/account/credits-history-list.tsx
/**
 * CreditsHistoryList — paginated credit adjustments ledger.
 * 19BN Phase 2: fetches /account/credits/history on mount.
 *
 * 19BN Phase D D2: now uses the existing accountApi.getCreditsHistory client
 * (was a raw fetch — same pattern as recharge-flow / invite-panel, fixed in
 * the same sweep).
 */

import { useEffect, useState } from 'react';
import { accountApi } from '../../api/account.js';
import type { CreditAdjustmentItem } from '../../api/account.js';

const REASON_LABELS: Record<string, string> = {
  signup_bonus: '注册赠送',
  invite_received: '邀请奖励',
  invite_sent: '邀请奖励',
  recharge: '充值',
  subscription_grant: '订阅月度',
  consume_ingest: '生成消耗',
  consume_regen: '重生成消耗',
  consume_repair: '修复消耗',
  consume_pending: '生成预扣',
  refund_failed: '失败退还',
  refund_overestimate: '差额退还',
  settle_failed: '结算失败',
  admin_adjust: '管理员调整',
};

export function CreditsHistoryList() {
  const [items, setItems] = useState<CreditAdjustmentItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      // 19BN Phase D D2: load via the existing accountApi client. Null =
      // anonymous / session expired — items stays empty, panel shows "暂无记录".
      try {
        const data = await accountApi.getCreditsHistory(1);
        if (data) setItems(data.items ?? []);
      } catch { /* non-critical */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-3xl)' }}>加载中...</div>;
  if (items.length === 0) return <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-3xl)' }}>暂无记录</div>;

  return (
    <div>
      {items.map((item) => {
        const isIncoming = item.delta > 0;
        return (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-lg)', padding: 'var(--space-lg) 0', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--text-md)', fontWeight: 500, color: 'var(--color-ink)' }}>
                {REASON_LABELS[item.reason] ?? item.reason}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                {new Date(item.createdAt).toLocaleString()} · 余额 {item.balanceAfter.toLocaleString()}
              </div>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-lg)', color: isIncoming ? 'var(--color-success-deep)' : 'var(--color-action)' }}>
              {isIncoming ? '+' : ''}{item.delta.toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
}
